import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { BarChart3, Trophy, TrendingUp, TrendingDown, BookOpen, Brain, ArrowUp, ArrowDown, Minus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WeeklyReport = ({ isDark }) => {
  const { token } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchReport(); }, []);

  const fetchReport = async () => {
    try {
      const res = await axios.get(`${API}/leaderboard/weekly-report`, { headers: { Authorization: `Bearer ${token}` } });
      setReport(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex justify-center h-64 items-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>;
  if (!report) return null;

  const wrc = report.comparison.win_rate_change;
  const mc = report.comparison.matches_change;

  return (
    <div className="max-w-2xl mx-auto space-y-4" data-testid="weekly-report">
      <h2 className={`text-xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
        <BarChart3 className="h-5 w-5 text-blue-600" /> Weekly Progress
      </h2>
      <p className="text-xs text-gray-500">{report.period.from} — {report.period.to}</p>

      {/* Stats grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-3 text-center">
          <Trophy className="h-5 w-5 mx-auto text-blue-500 mb-1" />
          <p className="text-2xl font-bold">{report.matches_played}</p>
          <p className="text-xs text-gray-500">Matches</p>
          <div className="flex items-center justify-center gap-0.5 mt-1">
            {mc > 0 ? <ArrowUp className="h-3 w-3 text-green-500" /> : mc < 0 ? <ArrowDown className="h-3 w-3 text-red-500" /> : <Minus className="h-3 w-3 text-gray-400" />}
            <span className={`text-[10px] font-medium ${mc > 0 ? 'text-green-600' : mc < 0 ? 'text-red-500' : 'text-gray-400'}`}>{mc > 0 ? `+${mc}` : mc} vs last week</span>
          </div>
        </CardContent></Card>

        <Card><CardContent className="p-3 text-center">
          <TrendingUp className="h-5 w-5 mx-auto text-green-500 mb-1" />
          <p className="text-2xl font-bold text-green-600">{report.wins}</p>
          <p className="text-xs text-gray-500">Wins</p>
        </CardContent></Card>

        <Card><CardContent className="p-3 text-center">
          <TrendingDown className="h-5 w-5 mx-auto text-red-400 mb-1" />
          <p className="text-2xl font-bold text-red-500">{report.losses}</p>
          <p className="text-xs text-gray-500">Losses</p>
        </CardContent></Card>

        <Card><CardContent className="p-3 text-center">
          <BarChart3 className="h-5 w-5 mx-auto text-blue-500 mb-1" />
          <p className="text-2xl font-bold text-blue-600">{report.win_rate}%</p>
          <p className="text-xs text-gray-500">Win Rate</p>
          <div className="flex items-center justify-center gap-0.5 mt-1">
            {wrc > 0 ? <ArrowUp className="h-3 w-3 text-green-500" /> : wrc < 0 ? <ArrowDown className="h-3 w-3 text-red-500" /> : <Minus className="h-3 w-3 text-gray-400" />}
            <span className={`text-[10px] font-medium ${wrc > 0 ? 'text-green-600' : wrc < 0 ? 'text-red-500' : 'text-gray-400'}`}>{wrc > 0 ? `+${wrc}` : wrc}% vs last week</span>
          </div>
        </CardContent></Card>
      </div>

      {/* Activity */}
      <div className="grid grid-cols-2 gap-3">
        <Card><CardContent className="p-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center"><BookOpen className="h-4 w-4 text-emerald-600" /></div>
          <div><p className="text-lg font-bold">{report.diary_entries}</p><p className="text-xs text-gray-500">Diary Entries</p></div>
        </CardContent></Card>
        <Card><CardContent className="p-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center"><Brain className="h-4 w-4 text-purple-600" /></div>
          <div><p className="text-lg font-bold">{report.ai_sessions}</p><p className="text-xs text-gray-500">AI Sessions</p></div>
        </CardContent></Card>
      </div>

      {report.matches_played === 0 && (
        <Card><CardContent className="py-8 text-center text-gray-500 text-sm">
          No matches this week. Get out there and play!
        </CardContent></Card>
      )}
    </div>
  );
};

export default WeeklyReport;
